/**
 * Client-Side Auth Module
 *
 * Provides SessionProvider, useSession, signIn, signOut, and getSession
 * for client-side auth operations.
 *
 * SessionProvider accepts an initial session from server layouts and
 * provides it via React context. useSession() returns { data, status, update }.
 */

'use client';

import * as React from 'react';
import type { SavvySession } from './types';

// ---- Context & Provider ----

interface SessionContextValue {
  data: SavvySession | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  update: () => Promise<void>;
}

const SessionContext = React.createContext<SessionContextValue>({
  data: null,
  status: 'loading',
  update: async () => {},
});

interface SessionProviderProps {
  session?: SavvySession | null;
  children: React.ReactNode;
}

/**
 * SessionProvider — wraps the app to provide session state via context.
 * Accepts an optional `session` prop from server layouts for initial state.
 */
export function SessionProvider({
  session: initialSession,
  children,
}: SessionProviderProps) {
  const [session, setSession] = React.useState<SavvySession | null>(
    initialSession ?? null
  );
  const [status, setStatus] = React.useState<SessionContextValue['status']>(
    initialSession === undefined
      ? 'loading'
      : initialSession
        ? 'authenticated'
        : 'unauthenticated'
  );

  // Sync session state when initialSession changes (e.g. after navigation)
  React.useEffect(() => {
    if (initialSession !== undefined) {
      setSession(initialSession ?? null);
      setStatus(initialSession ? 'authenticated' : 'unauthenticated');
    }
  }, [initialSession]);

  const update = React.useCallback(async () => {
    try {
      const res = await fetch('/api/auth/session?refresh=true');
      const data = await res.json();
      if (data?.user) {
        setSession(data);
        setStatus('authenticated');
      } else {
        setSession(null);
        setStatus('unauthenticated');
      }
    } catch {
      // Silent fail — session stays as-is
    }
  }, []);

  // Round 3 seo-fix-20: when the server layout doesn't pre-resolve the
  // session (it can't without tainting the (public) subtree dynamic and
  // killing Plan 7's ISR), hydrate the session on mount via the API.
  // Brief logged-out flash is acceptable; SEO crawlers see the static
  // logged-out shell, which is exactly what they should index anyway.
  React.useEffect(() => {
    if (initialSession !== undefined) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth/session');
        if (!res.ok) {
          if (!cancelled) setStatus('unauthenticated');
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data?.user) {
          setSession(data);
          setStatus('authenticated');
        } else {
          setSession(null);
          setStatus('unauthenticated');
        }
      } catch {
        if (!cancelled) setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
    // initialSession is intentionally only inspected once; subsequent
    // updates are handled by the sync effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = React.useMemo(
    () => ({ data: session, status, update }),
    [session, status, update]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

// ---- Hooks ----

/**
 * useSession — reactive access to the current session.
 * Returns { data: SavvySession | null, status, update }.
 */
export function useSession() {
  return React.useContext(SessionContext);
}

// ---- Client-side auth actions ----

type SignInResult = { ok: boolean; error?: string };

/**
 * signIn — authenticate with the given provider.
 *
 * Supports the same call patterns as NextAuth's signIn():
 * - signIn('credentials', { email, password, redirect: false })
 * - signIn('EngineRefreshTokenLogin', { refresh_token, redirect: false })
 * - signIn('AppleToken', { token, email, firstName, lastName })
 * - signIn('google') / signIn('google', { callbackUrl })
 * - signIn('facebook') / signIn('facebook', { callbackUrl })
 */
export async function signIn(
  provider: string,
  options?: Record<string, unknown>
): Promise<SignInResult | void> {
  // Social providers → full-page redirect to OAuth authorize endpoint
  if (provider === 'google' || provider === 'facebook') {
    const callbackUrl =
      (options?.callbackUrl as string) ||
      window.location.pathname + window.location.search;
    window.location.href = `/api/auth/authorize?provider=${provider}&callbackUrl=${encodeURIComponent(callbackUrl)}`;
    return; // Page navigates away
  }

  // Credential providers → API call
  const providerMap: Record<string, string> = {
    credentials: 'credentials',
    EngineRefreshTokenLogin: 'refreshToken',
    AppleToken: 'appleToken',
  };

  const { redirect: shouldRedirect, callbackUrl, ...credentials } =
    options || {};

  const res = await fetch('/api/auth/sign-in', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      provider: providerMap[provider] || provider,
      ...credentials,
    }),
  });

  const result: SignInResult = await res.json();

  if (result.ok && shouldRedirect !== false) {
    window.location.href = (callbackUrl as string) || '/';
  }

  return result;
}

/**
 * signOut — clear the session and optionally redirect.
 *
 * Supports the same call patterns as NextAuth's signOut():
 * - signOut() → clear session + redirect to '/'
 * - signOut({ callbackUrl: '/login', redirect: true })
 * - signOut({ redirect: false }) → clear session without navigation
 */
export async function signOut(
  options?: { callbackUrl?: string; redirect?: boolean }
): Promise<void> {
  await fetch('/api/auth/sign-out', { method: 'POST' });
  if (options?.redirect !== false) {
    window.location.href = options?.callbackUrl || '/';
  }
}

/**
 * getSession — fetch the current session from the API.
 * Used for polling (e.g. waitForSession).
 */
export async function getSession(): Promise<SavvySession | null> {
  try {
    const res = await fetch('/api/auth/session');
    if (!res.ok) return null;
    const data = await res.json();
    return data?.user ? data : null;
  } catch {
    return null;
  }
}
